<template>
  <ConverterInput
    class="my-2"
    label="Equipment Length"
    :model-value="scenarioCalculations.equipmentLengths.equipmentLengthFt.value"
    :base-unit="LengthUnits.Feet"
    :model="LargeLengthUnits.options()"
    :decimal-places="2"
    :update-unit="updateScenario"
    mode="convert"
    data-cy="equipment-length-input"
  />
  <ConverterInput
    class="my-2"
    label="Equipment Weight"
    :model-value="
      scenarioCalculations.equipmentWeights.equipmentWeightLbs.value
    "
    :base-unit="MassUnits.Pounds"
    :model="MassUnits.options()"
    :decimal-places="2"
    :update-unit="updateScenario"
    mode="convert"
    data-cy="equipment-weight-input"
  />
  <ConverterInput
    class="my-2"
    label="Total String Weight"
    :model-value="
      scenarioCalculations.equipmentWeights.totalStringWeightLbs.value
    "
    :base-unit="MassUnits.Pounds"
    :model="MassUnits.options()"
    :decimal-places="2"
    :update-unit="updateScenario"
    mode="convert"
  />
  <div v-bind="$attrs" ref="wrapper" class="calculator">
    <MultiSelect
      v-if="multiSelect"
      :model-value="activeCalculatorSections"
      placeholder="Select calculators..."
      display="chip"
      scroll-height="50vh"
      option-label="label"
      option-value="id"
      :options="filteredEquipmentCalculatorSections"
      data-cy="equipment-multiselect"
      @update:model-value="setActiveCalculatorSections($event)"
    />
    <Dropdown
      v-else
      :model-value="activeCalculatorSections"
      :options="filteredEquipmentCalculatorSections"
      option-label="label"
      option-value="id"
      append-to="body"
      data-cy="equipment-dropdown"
      @update:model-value="setActiveCalculatorSections([$event])"
    >
      <template #value="{ value }">
        <span>{{
          equipmentCalculatorSections.find(s => s.id === value[0])?.label
        }}</span>
      </template>
    </Dropdown>

    <div class="equipment-sections">
      <transition-group name="vertical-slide">
        <div
          v-for="id in activeCalculatorSections"
          :key="id"
          :ref="el => (calculatorRefs[id] = el as HTMLElement)"
          :class="{
            hidden: !activeCalculatorSections.includes(id)
          }"
        >
          <div class="equipment-section">
            <CalculatorSection
              :disable-close="!multiSelect"
              :section="equipmentCalculatorSections.find(s => s.id === id)"
              :update-scenario="updateScenario"
              :is-esp-on-shore="isEspOnShore"
              @select="onSectionSelect"
              @close="onSectionClose"
            />
          </div>
        </div>
      </transition-group>
      <div v-if="!activeCalculatorSections.length" class="heading-2">
        <span v-if="isEspOnShore">
          No calculators selected, maybe you want to see
          <a
            data-cy="equipment-suggestion-pumps"
            @click="
              pushToActiveCalculatorSections(EquipmentCalculatorSectionKey.PUMP)
            "
            >pumps</a
          >? or
          <a
            data-cy="equipment-suggestion-intakes"
            @click="
              pushToActiveCalculatorSections(
                EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
              )
            "
          >
            intakes
          </a>
          ?
        </span>
        <span v-else>
          No calculators selected, maybe you want to see
          <a
            data-cy="equipment-suggestion-pumps"
            @click="
              pushToActiveCalculatorSections(EquipmentCalculatorSectionKey.PUMP)
            "
            >pumps</a
          >?
        </span>
      </div>
    </div>
    <Diagnotics />
  </div>
</template>

<script setup lang="ts">
import ConverterInput from "@/components/ConverterInput.vue";
import type { PropType } from "vue";
import { computed, ref, watch } from "vue";
import Diagnotics from "./Diagnotics.vue";
import MultiSelect from "primevue/multiselect";
import {
  parametersKey,
  scenarioCalculationsKey,
  scenarioKey,
  sizingFormKey,
  sizingKey,
  wellPropertiesKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import CalculatorSection from "@/components/scenarios/calculator_sections/CalculatorSection.vue";
import {
  EquipmentWithNoCalculations,
  HIDDEN_WHEN_ESP,
  HIDDEN_WHEN_HPS,
  useEquipmentCalculatorSections
} from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import useCalculatorDomStore from "@/composables/scenarioCalculations/useCalculatorDomStore";
import type { RouteParams } from "vue-router";
import { useRouter } from "vue-router";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { useSettingsStore } from "@/store";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const emit = defineEmits<{
  (e: "section-select", value: string): void;
  (e: "section-close", value: string): void;
  (e: "update:active-sections", value: string[]): void;
}>();

const props = defineProps({
  multiSelect: {
    type: Boolean,
    default: false
  },
  routerParams: {
    type: Object as PropType<RouteParams>,
    required: true
  },
  updateScenario: {
    type: Boolean,
    default: true
  }
});

const {
  activeCalculatorSections,
  pushToActiveCalculatorSections,
  setActiveCalculatorSections,
  removeFromActiveCalculatorSections,
  sideCalcState
} = useSettingsStore();
const updateScenario = ref(props.updateScenario);
const calculatorRefs = ref<Record<string, HTMLElement | null>>({});

useCalculatorDomStore(calculatorRefs);

const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const parameters = injectStrict(parametersKey);
const wellProperties = injectStrict(wellPropertiesKey);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const sizingForm = injectStrict(sizingFormKey, null);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const equipmentCalculatorSections = useEquipmentCalculatorSections(
  scenarioCalculations,
  parameters,
  wellProperties,
  scenario,
  sizing
);

const filteredEquipmentCalculatorSections = computed(() =>
  equipmentCalculatorSections.value.filter(section => {
    if (
      EquipmentWithNoCalculations.includes(
        section.id as EquipmentCalculatorSectionKey
      )
    )
      return false;
    if (!isEspOnShore.value && HIDDEN_WHEN_HPS.includes(section.id))
      return false;
    if (isEspOnShore.value && HIDDEN_WHEN_ESP.includes(section.id))
      return false;
    return true;
  })
);

const router = useRouter();

const onSectionSelect = (id?: string) => {
  if (id) {
    emit("section-select", id);
    const section = equipmentCalculatorSections.value.find(s => s.id === id);
    if (section) {
      router.push({
        name: section.routeName,
        params: props.routerParams,
        hash: `#${section.hash}`
      });
    }
  }
};

watch(
  () => sideCalcState.value,
  newVal => {
    // Need to filter out sections here depending if hps or esp
    const filteredSections = newVal.filter(section => {
      if (
        isEspOnShore.value &&
        HIDDEN_WHEN_ESP.includes(section as EquipmentCalculatorSectionKey)
      ) {
        return false;
      }
      if (
        !isEspOnShore.value &&
        HIDDEN_WHEN_HPS.includes(section as EquipmentCalculatorSectionKey)
      ) {
        return false;
      }
      return true;
    });
    setActiveCalculatorSections([
      ...(filteredSections as EquipmentCalculatorSectionKey[])
    ]);
  },
  { immediate: true }
);

const onSectionClose = (id?: EquipmentCalculatorSectionKey | undefined) => {
  if (id) {
    emit("section-close", id);
    removeFromActiveCalculatorSections(id);
  }
};
</script>

<style lang="scss" scoped>
a {
  cursor: pointer;
}
$minWidth: max(15vw, 15em);
.calculator {
  display: grid;
  grid-template-columns: minmax($minWidth, 1fr);

  .equipment-sections {
    margin-top: 1em;
    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(auto-fit, minmax($minWidth, 1fr));
    align-items: flex-start;
    gap: 1.5em;
  }
}
:deep(.p-multiselect.p-multiselect-chip .p-multiselect-token) {
  background: var(--surface-400);
  color: var(--gray-800);
}
:deep(.p-multiselect.p-multiselect-chip .p-multiselect-token-icon) {
  color: var(--gray-700);
}
</style>
